import { forwardRef, useMemo, type CSSProperties, type HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../../utils/cn";

/**
 * HEX カラーを RGB に変換する
 * #RGB, #RRGGBB 形式に対応
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    const cleaned = hex.replace("#", "");
    if (cleaned.length === 3) {
        return {
            r: parseInt(cleaned[0] + cleaned[0], 16),
            g: parseInt(cleaned[1] + cleaned[1], 16),
            b: parseInt(cleaned[2] + cleaned[2], 16),
        };
    }
    if (cleaned.length === 6) {
        return {
            r: parseInt(cleaned.slice(0, 2), 16),
            g: parseInt(cleaned.slice(2, 4), 16),
            b: parseInt(cleaned.slice(4, 6), 16),
        };
    }
    return null;
};

/**
 * WCAG 相対輝度を計算し、背景色に対して見やすい文字色を返す
 * 明るい背景 → 黒文字、暗い背景 → 白文字
 */
const getContrastTextColor = (bgColor: string): string => {
    const rgb = hexToRgb(bgColor);
    if (!rgb) return "#FFFFFF";

    // sRGB → リニアRGB 変換後、WCAG 相対輝度を計算
    const toLinear = (c: number): number => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    const luminance = 0.2126 * toLinear(rgb.r) + 0.7152 * toLinear(rgb.g) + 0.0722 * toLinear(rgb.b);

    return luminance > 0.4 ? "#111827" : "#FFFFFF";
};

/**
 * ラベルのバリアント定義（CVA）
 */
const labelVariants = cva(
    /* 共通スタイル */
    ["inline-flex items-center font-medium rounded-full whitespace-nowrap"],
    {
        variants: {
            /** ラベルの外観 */
            variant: {
                error: "bg-red-600 text-white",
                warning: "bg-yellow-400 text-gray-900",
                neutral: "bg-gray-200 text-gray-900",
                info: "bg-brand-500 text-white",
                success: "bg-green-500 text-white",
            },
            /** ラベルのサイズ */
            size: {
                sm: "px-2 py-0.5 text-[10px]",
                md: "px-2.5 py-0.5 text-xs",
            },
        },
        defaultVariants: {
            variant: "neutral",
            size: "md",
        },
    }
);

/**
 * Label コンポーネントのProps
 */
export interface LabelProps
    extends Omit<HTMLAttributes<HTMLSpanElement>, "color">,
        VariantProps<typeof labelVariants> {
    /** カスタム背景色（HEX カラー値） */
    bgColor?: string;
    /**
     * カスタム文字色（CSS カラー値）
     * 省略時は bgColor の明るさから自動判定
     */
    textColor?: string;
}

/**
 * Label - ラベルコンポーネント
 *
 * @description
 * ステータスや優先度を視覚的に表示する小さなバッジ。
 * ホーム画面のタスク一覧やテーブル内で使用。
 *
 * - バリアント指定: `<Label variant="error">要対応</Label>`
 * - カスタムカラー: `<Label bgColor="#E05650">要対応</Label>`
 *   （文字色は背景色から自動判定。手動指定も可能）
 *
 * bgColor を指定した場合、variant の色は上書きされます。
 */
export const Label = forwardRef<HTMLSpanElement, LabelProps>(
    ({ variant, size, bgColor, textColor, className, style, children, ...props }, ref) => {
        const customStyle = useMemo<CSSProperties | undefined>(() => {
            if (!bgColor) return style;
            const resolvedTextColor = textColor ?? getContrastTextColor(bgColor);
            return {
                ...style,
                backgroundColor: bgColor,
                color: resolvedTextColor,
            };
        }, [bgColor, textColor, style]);

        return (
            <span
                ref={ref}
                className={cn(
                    labelVariants({ variant: bgColor ? null : variant, size }),
                    className
                )}
                style={customStyle}
                {...props}
            >
                {children}
            </span>
        );
    }
);

Label.displayName = "Label";

export { labelVariants };
export default Label;
